import React, {useEffect, useMemo, useState} from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";

import {colors} from "nxtconstants";
import {RangeSlider} from "components/ClientHome/CalculatorComponents";
import {
  calculateLumpsum,
  DEFAULT_INPUT,
  findClosestStepValue,
  INPUT_LIMITS
} from "./CalculatorLogic";
import {
  ChartHoverInfo,
  ChartLabel,
  MobileTabToggle,
  SummaryCards,
  ViewToggle,
  YearlyTable
} from "./CalculatorComponents";
import {
  CalculatorSection,
  CalculatorWrapper,
  ChartContainer,
  ControlPanel,
  PageTitle,
  ResultsPanel
} from "./CalculatorStyle";

const LumpsumCalculator = () => {
  const [investmentAmount, setInvestmentAmount] = useState(
    DEFAULT_INPUT.investmentAmount
  );
  const [durationYears, setDurationYears] = useState(DEFAULT_INPUT.durationYears);
  const [annualReturn, setAnnualReturn] = useState(DEFAULT_INPUT.annualReturn);
  const [activeView, setActiveView] = useState("graph");
  const [mobileTab, setMobileTab] = useState("input");
  const [chartKey, setChartKey] = useState(0);

  useEffect(() => {
    let timeoutId;

    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setChartKey((previous) => previous + 1);
      }, 150);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(timeoutId);
    };
  }, []);

  const result = useMemo(
    () =>
      calculateLumpsum({
        investmentAmount,
        durationYears,
        annualReturn
      }),
    [investmentAmount, durationYears, annualReturn]
  );

  const chartData = useMemo(
    () =>
      result.yearlyData.map((row) => ({
        year: `Year ${row.year}`,
        Investment: row.investedAmount,
        Returns: row.gain
      })),
    [result.yearlyData]
  );

  return (
    <CalculatorSection>
      <PageTitle>Lumpsum Calculator</PageTitle>

      <MobileTabToggle activeTab={mobileTab} onChange={setMobileTab} />

      <CalculatorWrapper>
        <ControlPanel $hidden={mobileTab === "result"}>
          <RangeSlider
            label="Investment Amount"
            value={investmentAmount}
            onChange={setInvestmentAmount}
            min={INPUT_LIMITS.investmentAmount.min}
            max={INPUT_LIMITS.investmentAmount.max}
            prefix="₹"
            findClosestStepValue={findClosestStepValue}
          />

          <RangeSlider
            label="Investment Period"
            value={durationYears}
            onChange={setDurationYears}
            min={INPUT_LIMITS.durationYears.min}
            max={INPUT_LIMITS.durationYears.max}
            step={1}
            suffix=" Years"
          />

          <RangeSlider
            label="Expected Rate of Return"
            value={annualReturn}
            onChange={setAnnualReturn}
            min={INPUT_LIMITS.annualReturn.min}
            max={INPUT_LIMITS.annualReturn.max}
            step={INPUT_LIMITS.annualReturn.step}
            suffix="%"
          />
        </ControlPanel>

        <ResultsPanel $hidden={mobileTab === "input"}>
          <SummaryCards
            futureValue={result.futureValue}
            gain={result.gain}
            totalInvested={result.totalInvested}
          />

          <ViewToggle activeView={activeView} onChange={setActiveView} />

          {activeView === "graph" ? (
            <ChartContainer>
              <ResponsiveContainer width="100%" height={400} key={chartKey}>
                <BarChart
                  barCategoryGap="10%"
                  barGap={2}
                  data={chartData}
                  margin={{top: 10, right: 5, left: 5, bottom: 30}}
                >
                  <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    axisLine={false}
                    dataKey="year"
                    tick={{fontSize: 12, fill: "#9ca3af"}}
                    tickLine={false}
                  />
                  <YAxis
                    axisLine={false}
                    domain={[0, "dataMax + 20"]}
                    tick={{fontSize: 12, fill: "#9ca3af"}}
                    tickCount={5}
                    tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}k`}
                    tickLine={false}
                  />
                  <Tooltip
                    content={<ChartHoverInfo />}
                    cursor={colors.chartCursor}
                  />
                  <Legend
                    align="center"
                    content={<ChartLabel />}
                    layout="horizontal"
                    verticalAlign="bottom"
                  />
                  <Bar
                    activeBar={{fill: colors.primary, opacity: 1}}
                    dataKey="Investment"
                    fill={colors.primary}
                    name="Invested Amount"
                    radius={[0, 0, 2, 2]}
                    stackId="a"
                  />
                  <Bar
                    activeBar={{
                      fill: colors.chartAccent,
                      opacity: 1,
                      stroke: "#ffffff",
                      strokeWidth: 1
                    }}
                    dataKey="Returns"
                    fill={colors.chartAccent}
                    name="Estimated Return"
                    radius={[3, 3, 0, 0]}
                    stackId="a"
                    stroke="#ffffff"
                    strokeWidth={1}
                  />
                </BarChart>
              </ResponsiveContainer>
            </ChartContainer>
          ) : (
            <YearlyTable yearlyData={result.yearlyData} />
          )}
        </ResultsPanel>
      </CalculatorWrapper>
    </CalculatorSection>
  );
};

export default LumpsumCalculator;
